/**
 * Lightweight in-house analytics utility.
 * Tracks events, page views, scroll depth, and user activity.
 * Supports authenticated user tracking for admin analytics.
 */

// Use the same API base as the rest of the app (includes /v1 suffix)
const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? 'http://localhost:8005/v1'

// User context for authenticated tracking
let currentUserId: number | null = null
let userIdSetPromise: Promise<void> | null = null
let resolveUserIdSet: (() => void) | null = null
let userIdFetchAttempted = false

// Create a promise that resolves when user ID is first set
userIdSetPromise = new Promise((resolve) => {
  resolveUserIdSet = resolve
})

// Retry fetching user ID if not set yet (handles race conditions)
let userIdRetryCallback: (() => Promise<void>) | null = null

/**
 * Register a callback to retry fetching user ID.
 * This is called by useAnalyticsUserSync to allow retries.
 */
export function registerUserIdRetry(callback: () => Promise<void>): void {
  userIdRetryCallback = callback
}

/**
 * Set the current user ID for tracking authenticated user activity.
 * Call this when user logs in/out.
 */
export function setAnalyticsUser(userId: number | null): void {
  currentUserId = userId
  userIdFetchAttempted = true
  // Resolve the promise when user ID is set for the first time
  if (userId !== null && resolveUserIdSet) {
    resolveUserIdSet()
    resolveUserIdSet = null
  }
}

/**
 * Get the current analytics user ID.
 */
export function getAnalyticsUserId(): number | null {
  return currentUserId
}

// Generate or retrieve session ID
function getSessionId(): string {
  const key = 'ss_session_id'
  let sessionId = sessionStorage.getItem(key)
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStorage.setItem(key, sessionId)
  }
  return sessionId
}

interface EventProperties {
  [key: string]: string | number | boolean | null | undefined
}

interface AnalyticsEvent {
  event_name: string
  properties?: EventProperties
  session_id: string
  page_url: string
  referrer: string
  user_id?: number
}

// Queue for batching events
let eventQueue: AnalyticsEvent[] = []
let flushTimeout: ReturnType<typeof setTimeout> | null = null

/**
 * Send events to the backend.
 * Uses beacon API for reliability on page unload.
 */
async function sendEvents(events: AnalyticsEvent[]): Promise<void> {
  if (events.length === 0) return

  const endpoint = events.length === 1
    ? `${API_BASE}/analytics/event`
    : `${API_BASE}/analytics/events`

  const body = events.length === 1
    ? JSON.stringify(events[0])
    : JSON.stringify({ events })

  // Use sendBeacon for reliability (works even on page unload)
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    navigator.sendBeacon(endpoint, blob)
  } else {
    // Fallback to fetch
    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      })
    } catch {
      // Silently fail - analytics should never break the app
    }
  }
}

/**
 * Flush queued events to the server.
 * Re-checks for user_id at send time in case it was set after queueing.
 * Waits for user ID with retry logic to handle race conditions.
 */
async function flushEvents(): Promise<void> {
  if (eventQueue.length === 0) return

  if (flushTimeout) {
    clearTimeout(flushTimeout)
    flushTimeout = null
  }

  // If events don't have user_id and we're still waiting for it, wait and retry
  const hasEventsWithoutUserId = eventQueue.some(e => !e.user_id)
  if (hasEventsWithoutUserId && !currentUserId) {
    // First, wait for the initial promise (up to 3 seconds)
    if (userIdSetPromise) {
      await Promise.race([
        userIdSetPromise,
        new Promise(resolve => setTimeout(resolve, 3000))
      ])
    }

    // If still no user ID and we haven't tried fetching yet, trigger a retry
    if (!currentUserId && !userIdFetchAttempted && userIdRetryCallback) {
      try {
        await Promise.race([
          userIdRetryCallback(),
          new Promise(resolve => setTimeout(resolve, 2000))
        ])
      } catch {
        // Retry failed, proceed without user ID
      }
    }
  }

  // Re-apply user_id to events that don't have it yet
  // This handles the race condition where user ID is set after event was queued
  const events = eventQueue.map(event => {
    if (!event.user_id && currentUserId) {
      return { ...event, user_id: currentUserId }
    }
    return event
  })
  eventQueue = []

  sendEvents(events)
}

/**
 * Queue an event for sending.
 * Events are batched and sent after 1 second of inactivity.
 */
function queueEvent(event: AnalyticsEvent): void {
  // Add user ID if available
  if (currentUserId) {
    event.user_id = currentUserId
  }

  eventQueue.push(event)

  // Reset flush timer
  if (flushTimeout) {
    clearTimeout(flushTimeout)
  }

  // Flush after 1 second of inactivity, or immediately if queue is large
  if (eventQueue.length >= 10) {
    flushEvents()
  } else {
    flushTimeout = setTimeout(flushEvents, 1000)
  }
}

/**
 * Track a custom event.
 */
export function trackEvent(name: string, properties?: EventProperties): void {
  queueEvent({
    event_name: name,
    properties,
    session_id: getSessionId(),
    page_url: window.location.pathname,
    referrer: document.referrer,
  })
}

/**
 * Track a page view.
 */
export function trackPageView(page?: string): void {
  trackEvent('page_view', {
    page: page || window.location.pathname,
    title: document.title,
  })
}

/**
 * Track scroll depth (call at thresholds like 25%, 50%, 75%, 100%).
 */
export function trackScrollDepth(percentage: number): void {
  trackEvent('scroll_depth', { percentage })
}

/**
 * Track CTA button clicks with location context.
 */
export function trackCTAClick(location: string, variant?: string): void {
  trackEvent('cta_click', {
    location,
    variant,
  })
}

/**
 * Track section visibility (when user scrolls to a section).
 */
export function trackSectionView(sectionName: string): void {
  trackEvent('section_view', { section: sectionName })
}

// Flush events on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushEvents)
  window.addEventListener('pagehide', flushEvents)
}

/**
 * Hook for tracking scroll depth.
 * Returns a function to call in useEffect.
 */
export function createScrollTracker(): () => void {
  const tracked = new Set<number>()
  const thresholds = [25, 50, 75, 100]

  return () => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercent = scrollHeight > 0
        ? Math.round((window.scrollY / scrollHeight) * 100)
        : 0

      thresholds.forEach((threshold) => {
        if (scrollPercent >= threshold && !tracked.has(threshold)) {
          tracked.add(threshold)
          trackScrollDepth(threshold)
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }
}

// ============================================================================
// Feature Usage Tracking (for admin analytics)
// ============================================================================

// Heartbeat state
let heartbeatInterval: ReturnType<typeof setInterval> | null = null
let currentFeature: string | null = null
let featureStartTime: number | null = null

/**
 * Track when user enters a feature/page.
 * Returns a cleanup function that tracks exit with duration.
 *
 * @example
 * useEffect(() => {
 *   const cleanup = trackFeatureEnter('Value Bets')
 *   return cleanup
 * }, [])
 */
export function trackFeatureEnter(feature: string): () => void {
  const startTime = Date.now()

  trackEvent('feature_enter', { feature })

  // Start heartbeat for this feature
  startHeartbeat(feature)

  // Return cleanup function
  return () => {
    const duration = Date.now() - startTime
    trackEvent('feature_exit', {
      feature,
      duration_ms: duration,
      duration_seconds: Math.round(duration / 1000),
    })
    stopHeartbeat()
  }
}

/**
 * Start sending heartbeat events for time tracking.
 * Heartbeats are sent every 30 seconds while page is focused.
 */
export function startHeartbeat(feature: string): void {
  currentFeature = feature
  featureStartTime = Date.now()

  // Clear any existing heartbeat
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
  }

  // Send heartbeat every 30 seconds while page is focused
  heartbeatInterval = setInterval(() => {
    if (document.hasFocus() && currentFeature) {
      trackEvent('heartbeat', {
        feature: currentFeature,
        elapsed_seconds: featureStartTime ? Math.round((Date.now() - featureStartTime) / 1000) : 0,
      })
    }
  }, 30000)
}

/**
 * Stop heartbeat tracking.
 */
export function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
  currentFeature = null
  featureStartTime = null
}

/**
 * Track a user action within a feature.
 *
 * @example
 * trackUserAction('builder_add_leg', { market: 'BTTS', fixture_id: 123 })
 */
export function trackUserAction(action: string, details?: EventProperties): void {
  trackEvent(action, {
    ...details,
    feature: currentFeature,
  })
}

// Clean up heartbeat on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (currentFeature && featureStartTime) {
      const duration = Date.now() - featureStartTime
      // Send final event synchronously via beacon
      const event = {
        event_name: 'feature_exit',
        properties: {
          feature: currentFeature,
          duration_ms: duration,
          duration_seconds: Math.round(duration / 1000),
        },
        session_id: getSessionId(),
        page_url: window.location.pathname,
        referrer: document.referrer,
        user_id: currentUserId || undefined,
      }
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' })
      navigator.sendBeacon(`${API_BASE}/analytics/event`, blob)
    }
    stopHeartbeat()
  })
}
